// ============================================================================
// CHECK-IN TYPES
// ============================================================================
// Mirrors crs.Core.DTOs.CheckInModule.

export interface CheckInRoom {
  id: string;
  checkInId: string;
  bookingId: string;
  roomId: string;
  roomNumber: string;
  roomCategoryId: string;
  categoryCode?: string;
  categoryName?: string;
}

/**
 * Read-side projection of one ID document captured for a guest at a booking.
 * Mirrors crs.Core.DTOs.CheckInModule.GuestDocumentDto.
 */
export interface GuestDocument {
  id: string;
  guestId?: string;
  bookingId: string;
  documentType: string;
  /** Nullable since fixes/013 — operators may capture an ID without a printed number. */
  documentNumber?: string;
  documentPath?: string;
  createdAt: string;
}

export interface CheckIn {
  id: string;
  bookingId: string;
  checkInDate: string; // ISO date
  checkInTime: string; // HH:MM:SS
  checkInNotes?: string;
  /** Primary ID type — first complete entry of `documents`, kept for legacy single-doc readers. */
  idType?: string;
  idNumber?: string;
  idDocumentPath?: string;
  /** Guest Registration Card number (GRC-{PROP}-NNNNNN), generated at check-in. */
  grcNumber?: string;
  /** Primary guest's country — drives the Resident/Foreigner occupancy split. */
  nationality?: string;
  numMale?: number;
  numFemale?: number;
  /** Actual on-site pax captured at check-in (D3 Phase A follow-up). */
  numAdults?: number;
  numChildren?: number;
  numInfants?: number;
  checkedInBy?: string;
  checkedInByName?: string;
  createdAt: string;
  rowVersion: number;
  rooms: CheckInRoom[];
  /** All ID documents captured for the guest on this booking. Empty when no IDs were captured. */
  documents: GuestDocument[];
  // ── Guest Registration Card (read-side, matches CheckInDto) ────────────
  purposeOfVisit?: string;
  purposeOfVisitNote?: string;
  nextDestination?: string;
  grcAddress?: string;
  grcMobile?: string;
  grcEmail?: string;
  /** Registration-card completeness (GRC-3). */
  grc?: GrcStatus | null;
  /** Scan of the signed card on file (GRC-4). */
  hasSignedCard?: boolean;
  grcScanAt?: string | null;
  grcScanByName?: string | null;
}

/**
 * A stay's registration-card completeness (GRC-3). `pending` = the property
 * completes cards after check-in (checkin.grc_complete_later) and something is
 * missing; `formCDueAt` = arrival + 24 h for a foreign national whose card is pending.
 */
export interface GrcStatus {
  completeLater: boolean;
  missing: string[];
  pending: boolean;
  isForeign: boolean;
  formCDueAt: string | null;
}

/** "Form C due 24-Oct 14:10" / "Form C overdue since …". */
export const formCText = (due: string | null | undefined): { text: string; overdue: boolean } | null => {
  if (!due) return null;
  const d = new Date(due);
  const when = d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
  const overdue = d.getTime() < Date.now();
  return { text: overdue ? `Form C overdue since ${when}` : `Form C due ${when}`, overdue };
};

export interface CheckInRoomAssignmentRequest {
  roomId: string;
}

/**
 * One ID document captured at check-in. A booking may carry several
 * (Aadhar + Passport + visa). Each is saved as a row in
 * `booking.tbl_guest_document`; the first also lands on `tbl_checkin` as the
 * primary ID for legacy single-doc readers.
 */
export interface IdDocumentRequest {
  idType: string;
  /** Optional — many real-world captures (consent forms, photo-only IDs) have no printed number. */
  idNumber?: string;
  /** Optional — type/number can be recorded without a scan. The property's `checkin.require_id_document` config demands at least one entry WITH a file. */
  idDocumentPath?: string;
}

/**
 * One person on the stay, captured at check-in (D3 Guest Model Phase A).
 * Matches crs.Core/DTOs/CheckInModule/CheckInGuestRequest.cs. Rows with `id`
 * update the existing booking-guest row; rows without insert a new one.
 */
export interface CheckInGuestRequest {
  /** Existing booking guest row id — undefined for a newly added guest. */
  id?: string;
  isPrimary: boolean;
  firstName: string;
  lastName?: string;
  /** Adult / Child / Infant. */
  guestType: string;
  age?: number;
  /** MALE / FEMALE / OTHER. */
  gender?: string;
  /** Country name ("India") — COUNTRY enum value. */
  nationality?: string;
  /** Residence state — INDIAN_STATE enum value when nationality is India. */
  state?: string;
  /**
   * Contact for THIS guest. Mandatory on the primary guest — a booking with four
   * parties needs each of them reachable, not one number for the whole stay.
   */
  phone?: string;
  email?: string;
  idType?: string;
  idNumber?: string;
  /** Optional uploaded ID scan for this guest. */
  idDocumentPath?: string;
}

export interface QuickCheckInRequest {
  checkInDate: string; // ISO date
  checkInTime: string; // HH:MM:SS
  checkInNotes?: string;
  /** @deprecated use `idDocuments`; kept for backwards compat. Server auto-populates from the first entry of `idDocuments` when set. */
  idType?: string;
  /** @deprecated use `idDocuments`. */
  idNumber?: string;
  /** @deprecated use `idDocuments`. */
  idDocumentPath?: string;
  /** Multi-doc capture — legacy booking-level rows; the Guests panel sends `guests` instead. */
  idDocuments?: IdDocumentRequest[];
  /**
   * Per-stay guest rows (primary + accompanying). When present the server
   * derives nationality/numMale/numFemale from these rows where they carry
   * the data, and the primary guest's ID mirrors onto tbl_checkin.
   */
  guests?: CheckInGuestRequest[];
  /** Primary guest's country — Resident vs Foreigner occupancy split. */
  nationality?: string;
  /** Adult male pax count (occupancy gender summary). Optional. */
  numMale?: number;
  /** Adult female pax count (occupancy gender summary). Optional. */
  numFemale?: number;
  /** Actual adult count on site — keyed when guests aren't entered individually. */
  numAdults?: number;
  /** Actual child count on site. */
  numChildren?: number;
  /** Actual infant count on site — bookings never carry infants; this is the only record. */
  numInfants?: number;
  /**
   * Why this check-in is allowed to exceed the booked rooms' occupancy ceiling
   * (category max_occupancy). Required by the server when actual adults+children
   * exceed capacity; NULL/omitted when within limits.
   */
  occupancyOverrideReason?: string;
  /**
   * MAP stays: the major meal the guests expect to take — LUNCH, DINNER or EITHER.
   * Omit for the plan's expected meal. Predicts the kitchen forecast and the
   * restaurant follow-up only; changed later from In-House Guests → Meals.
   */
  majorMealPref?: 'LUNCH' | 'DINNER' | 'EITHER';
  // ── Guest Registration Card (CRS-CheckIn-GRC-Redesign.md) ──────────────
  /** Operator override for properties using a pre-printed GRC book. Omit to have one generated. */
  grcNumber?: string;
  /** Value from the CHECKIN_POLICY config list, or 'Other'. */
  purposeOfVisit?: string;
  /** Free text — only sent when purposeOfVisit is 'Other'. */
  purposeOfVisitNote?: string;
  /** "Proceeding To". */
  nextDestination?: string;
  /**
   * Contact details as declared on this card. Snapshotted onto the check-in so a
   * later profile edit cannot rewrite a signed GRC, and written back to the guest
   * profile so the next stay auto-fills.
   */
  grcAddress?: string;
  grcMobile?: string;
  grcEmail?: string;
  rooms: CheckInRoomAssignmentRequest[];
  /**
   * Explicit confirmation that every UNASSIGNED booked room is a no-show
   * (partial arrival). BR-CHKIN-002 revised: assigned + no-show = booked,
   * always — no ambiguous leftovers. The per-line counts are derived
   * server-side from what was not assigned; marking releases the remaining
   * nights and raises a NO_SHOW_PRICING work item.
   */
  acceptNoShowForUnassigned?: boolean;
  /** The desk's account of the no-show — required when rooms are unassigned. */
  noShowRemarks?: string;
  /**
   * The desk has seen the projected shortage across this stay and accepts it
   * (BR-SHORT-002). Covers EVERY night, including the arrival night.
   *
   * A shortage never blocks a check-in, but it is never taken silently either —
   * without this flag the server refuses and names the affected nights.
   *
   * The arrival night is included because what guarantees a room tonight is the
   * per-room availability check, not category inventory: every assigned room is
   * verified free, and a category move targets the category those rooms belong
   * to. An arrival-night shortfall means the category owes a room to a booking
   * due in later today — a sharper call than a shortage weeks out, so it gets
   * its own sentence, but still the desk's to make.
   */
  acceptShortage?: boolean;
}

/**
 * Nightly inventory position across a booking's stay, per room category —
 * `GET /CheckIn/{bookingId}/inventory-impact`. Lets the desk see a projected
 * shortage before committing the check-in rather than after.
 */
export interface CheckInInventoryImpact {
  bookingId: string;
  /** First night of the stay. */
  stayFrom: string;
  /** Departure date — exclusive; this night is not occupied. */
  stayTo: string;
  /**
   * The one night that hard-gates the check-in. Every other night of the stay is
   * notify-and-proceed.
   */
  arrivalNight: string;
  categories: CheckInCategoryImpact[];
}

export interface CheckInCategoryImpact {
  roomCategoryId: string;
  categoryCode: string;
  categoryName: string;
  nights: CheckInInventoryNight[];
}

export interface CheckInInventoryNight {
  night: string;
  /**
   * Rooms this category could take on this night if the booking being checked in
   * did not exist. Compare against the TOTAL rooms the booking will hold in the
   * category after assignment — not the delta — because the booking's own rooms
   * have been added back.
   */
  availableRooms: number;
  /**
   * Rooms already committed beyond inventory on this night, including this
   * booking. Nonzero means the night is short before the desk does anything.
   */
  oversoldRooms: number;
}

/**
 * Post-check-in amendment (amend mode) — matches
 * crs.Core/DTOs/CheckInModule/AmendCheckInRequest.cs. Rooms, GRC number and
 * anything with money are deliberately absent: Room Move, Extend Stay and the
 * booking-staff flows own those.
 */
export interface AmendCheckInRequest {
  checkInDate: string; // ISO date
  checkInTime: string; // HH:MM:SS
  checkInNotes?: string;
  nationality?: string;
  numMale?: number;
  numFemale?: number;
  numAdults?: number;
  numChildren?: number;
  numInfants?: number;
  /**
   * FULL replacement guest set — rows with `id` update, rows without insert,
   * stored rows missing from the list are deactivated. Omit to leave guest
   * rows untouched.
   */
  guests?: CheckInGuestRequest[];
  occupancyOverrideReason?: string;
  purposeOfVisit?: string;
  purposeOfVisitNote?: string;
  nextDestination?: string;
  grcAddress?: string;
  grcMobile?: string;
  grcEmail?: string;
}

export interface MarkNoShowRequest {
  reason: string;
}

/**
 * Room-level no-show inside a partially-arrived CHECKED_IN booking — distinct
 * from the booking-level NO_SHOW status (nobody arrived). Marking releases the
 * remaining nights back to inventory and raises a NO_SHOW_PRICING work item
 * for booking staff (charge decided in the Rate Worksheet, policy-guided).
 */
export interface MarkNoShowRoomsRequest {
  lines: { bookingDetailId: string; rooms: number }[];
  /** The desk's account of the no-show — mandatory. */
  remarks: string;
}

export interface AvailableRoom {
  roomId: string;
  roomNumber: string;
  roomCategoryId: string;
  categoryCode?: string;
  categoryName?: string;
  floorNumber?: string;
  /** Housekeeping status (ROOM_AVAILABLE / ROOM_DIRTY / ROOM_INSPECTED …) — dirty rooms are assignable but flagged. */
  housekeepingStatusCode?: string;
  housekeepingStatusName?: string;
}

/**
 * Front-desk arrival changes (matches ArrivalChangeRequest.cs).
 *
 * Deliberately narrow: only what varies when the guest walks up. Property,
 * dates, source, guest and the booking discount are re-read server-side from
 * the stored booking and cannot be altered through this path — so the desk
 * never needs (or gains) booking-edit permission.
 */
export interface ArrivalChangeLine {
  bookingDetailId: string;
  /** Adults actually arriving; omit to leave unchanged. */
  numAdults?: number;
  /** Children actually arriving; omit to leave unchanged. */
  numChildren?: number;
  /**
   * Rooms actually taken; omit to leave unchanged. Increasing consumes inventory
   * for every remaining night, decreasing releases it — a reduction is
   * commercially a partial cancellation, and is NOT the same as leaving a room
   * unallocated at check-in, which means "not arrived yet".
   */
  numberOfRooms?: number;
  /** Meal plan the guest is taking — moves on the booking so the kitchen sees it. */
  mealPlanId?: string;
  /** Agreed room rate per room-night; recorded as an adjustment, never an overwrite. */
  agreedRatePerRoom?: number;
  /** Agreed meal rate per adult-night. */
  agreedMealAdultRate?: number;
  /** Reason — required by the server for a discount. */
  adjustmentRemarks?: string;
}

export interface ArrivalChangeRequest {
  lines: ArrivalChangeLine[];
  /** Arrival add-ons (extra bed, transfer…). Replaces the booking's addon set. */
  addons?: Array<{ id?: string; addonId: string; unitAmount: number; quantity: number; remarks?: string }>;
  remarks?: string;
}